<template>
    <div class="clients__page clients__page--view">
        <template v-if="pageLoading">
            <PageSpinner />
        </template>

        <template v-else-if="loadingErr">
            <EmptyPage state="error" @action="handleFetchGoals" />
        </template>

        <template v-else>
            <div>
                <div>
                    <div class="mt--5">
                        <div class="flex align-center justify-content-between w-100">
                            <div>
                                <FormLabel 
                                    label-type="tertiary" 
                                    title="Goals" 
                                    class="text--medium mb--0"
                                />
                                
                                <p class="text--sm text-gray-600 text--normal mt--3">
                                    Set goals to measure your progress, narrow focus, and boost productivity. Find motivation through goal setting today! 
                                </p>
                            </div>
                
                            <div class="form-check form-switch">
                                <input
                                    id="flexSwitchCheckDefault" v-model="form.isGoalsEnabled" class="form-check-input"
                                    type="checkbox" role="switch"
                                >
                                <label class="form-check-label" for="flexSwitchCheckDefault" />
                            </div>
                        </div>
                    </div>

                    <div class="grid gap-3">
                        <div v-for="item in groupByKeys" :key="item" class="mt--20">
                            <p class="settings__goals__item__title text-gray-600">
                                {{ item }}
                            </p>
    
                            <div class="settings__goals__item__inner mt--3">
                                <p class="mb--10 text--sm">
                                    Frequency
                                </p>
                                <div v-for="freq in groupedListByType[item]" :key="freq.name" class="form-check form-check-inline">
                                    <input
                                        id="frequencyFor" v-model="freq.isActive" class="form-check-input" 
                                        type="checkbox" :value="freq.isActive"
                                    >
                                    <FormLabel 
                                        :title="freq.name" 
                                        for="frequencyFor"
                                        class="text--sm form-check-label text--capitalize" 
                                    />
                                </div>
                            
                                <div v-for="freq in groupedListByType[item]" :key="freq.name" class="mt--20 w--40">
                                    <p class="mb--10 text--sm">
                                        Target for {{ freq.name }} {{ item }}
                                    </p>
                                    <FormInput
                                        :id="`${freq.name}Tasks`" 
                                        v-model="freq.value" 
                                        size="sm" 
                                        type="number"
                                        :disabled="!form.isGoalsEnabled"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
    
                <div>
                    <div class="settings__auth__header mt--30">
                        <div class="flex align-center justify-content-between w-100">
                            <FormLabel 
                                label-type="menu" 
                                title="Productivity" 
                                class="text--base" 
                            />
                            <div class="form-check form-switch">
                                <input
                                    id="productivityGoalCheck" v-model="form.isProductivityEnabled" class="form-check-input"
                                    type="checkbox" role="switch"
                                >
                                <FormLabel 
                                    title="Productivity" 
                                    for="productivityGoalCheck"
                                    class="text--sm form-check-label" 
                                />
                            </div>
                        </div>
                    </div>
                    <div class="mt--20">
                        <p class="mb--10 text--sm">
                            Working days
                        </p>
                        <div v-for="day in daysOfWeek" :key="day" class="form-check form-check-inline">
                            <input
                                id="workingDays" v-model="form.productivity.workingDays" 
                                :value="day" class="form-check-input" type="checkbox" :disabled="!form.isProductivityEnabled"
                            >
                            <FormLabel 
                                :title="day" 
                                for="workingDays"
                                class="text--sm form-check-label text--capitalize" 
                            />
                        </div>
                    </div>

                    <div class="mt--15">
                        <p class="mb--10 text--sm">
                            Status
                        </p>
                        <div v-for="status in ['work', 'vacation']" :key="status" class="form-check form-check-inline">
                            <input
                                id="statusGoal" v-model="form.productivity.status" class="form-check-input" type="radio"
                                name="inlineRadioOptions" :value="status" :disabled="!form.isProductivityEnabled"
                            >
                            <FormLabel 
                                :title="status" 
                                for="statusGoal"
                                class="text--sm form-check-label text--capitalize" 
                            />
                        </div>
                    </div>
                </div>
    
                <div class="settings__auth__header mt--30" />

                <div class="flex justify-content-end w-100 mt--20 mb--15">
                    <BaseButton label="Save" :loading="updateLoading" @submit="handleUpdate" />
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import {
    BaseButton,
    // CustomLink,
    EmptyPage,
    PageSpinner,
    FormInput,
    FormLabel,
} from "@/components/ui/index";
import { groupList } from "@/utils/helpers";
import { daysOfWeek } from "@/utils/dummy";

export default {
    name: "GoalsAndProductivity",

    components: {
        BaseButton,
        // CustomLink,
        PageSpinner,
        EmptyPage,
        FormInput,
        FormLabel,
    },

    data() {
        return {
            daysOfWeek,
            form: {
                _id: "",
                isGoalsEnabled: false,
                isProductivityEnabled: false,
                goals: [],
                productivity: {
                    status: "",
                    workingDays: [],
                },
            },
            loadingErr: false,
            pageLoading: false,
            updateLoading: false,
        };
    },

    computed: {
        goals() {
            return this.$store.getters["settings/goals"];
        },

        isGoalsEnabled() {
            return this.$store.getters["settings/isGoalsEnabled"];
        },

        isProductivityEnabled() {
            return this.$store.getters["settings/isProductivityEnabled"];
        },

        goalId() {
            return this.$store.getters["settings/goalId"];
        },

        productivity() {
            return this.$store.getters["settings/productivity"];
        },

        groupedListByType() {
            if (!this.form.goals.length) {
                return [];
            }

            const arr = this.groupList(this.form.goals, (item) => {
                const name = item.type;
                const filter = name;

                return filter;
            });

            return Object.fromEntries(arr);
        },

        groupByKeys() {
            return Object.keys(this.groupedListByType);
        },
    },

    async mounted() {
        if (!this.goals.length) {
            await this.handleFetchGoals();
        } else {
            this.form = {
                ...this.form,
                _id: this.goalId,
                goals: this.goals,
                isGoalsEnabled: this.isGoalsEnabled,
                isProductivityEnabled: this.isProductivityEnabled,
                productivity: this.productivity,
            };
        }
    },

    methods: {
        groupList,

        async handleFetchGoals() {
            try {
                this.loadingErr = false;
                this.pageLoading = true;

                const res = await this.$store.dispatch("settings/fetchGoals");
                this.form = {
                    ...this.form,
                    ...res,
                };
            } catch (error) {
                this.loadingErr = true;
            } finally {
                this.pageLoading = false;
            }
        },

        async handleUpdate() {
            try {
                this.updateLoading = true;

                const payload = this.form;
                await this.$store.dispatch("settings/updateGoals", payload);
                this.$toast.success("Goals updated");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.updateLoading = false;
            }
        },
    },
};
</script>